import { useCallback, useEffect, useRef, useState } from "react";
import { apiDelete, apiGet, apiPatch, apiPut, ApiError } from "../api/client";
import { ApiKeyField } from "./settings/ApiKeyField";
import {
  FM_REFRESH_MAX, FM_REFRESH_MIN, FM_SETTINGS_INTRO, isoDateTimeText, parseRefreshMinutes, resultText, stateText,
  type FreqMapperStatus,
} from "../lib/freqmapperCoverage";

// Settings, FreqMapper: the API key (masked, set, replace or clear), the on/off switch for the map layer, how often to refresh,
// and what the last fetches did. Saves apply at once: no restart. The key is sent once and never shown again; the page only
// ever learns whether one is set.

type Note = { kind: "ok" | "error"; text: string } | null;

function errorText(e: unknown): string {
  if (e instanceof ApiError) {
    const m = /—\s(.+)$/.exec(e.message);
    if (m) return m[1];
    if (e.status === 403) return "Only an admin can change this.";
    return "That did not save. Try again.";
  }
  return "That did not save. Try again.";
}

export function FreqMapperSettings() {
  const [status, setStatus] = useState<FreqMapperStatus | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [minutesText, setMinutesText] = useState("");
  const [note, setNote] = useState<Note>(null);
  const [busy, setBusy] = useState(false);
  const minutesEdited = useRef(false);

  const load = useCallback(async () => {
    try {
      const s = await apiGet<FreqMapperStatus>("/api/freqmapper/status");
      setStatus(s);
      setLoadFailed(false);
      if (!minutesEdited.current) setMinutesText(String(s.refresh_minutes));
    } catch {
      setLoadFailed(true);
    }
  }, []);

  useEffect(() => {
    load();
    const t = window.setInterval(load, 10_000);
    return () => window.clearInterval(t);
  }, [load]);

  async function run(action: () => Promise<unknown>, okText: string) {
    setBusy(true);
    setNote(null);
    try {
      await action();
      setNote({ kind: "ok", text: okText });
      await load();
    } catch (e) {
      setNote({ kind: "error", text: errorText(e) });
    } finally {
      setBusy(false);
    }
  }

  async function saveKey(key: string) {
    await run(() => apiPut("/api/freqmapper/key", { api_key: key }), "Key saved. Stored coverage from the old key was removed.");
  }

  async function clearKey() {
    await run(() => apiDelete("/api/freqmapper/key"), "Key cleared. Stored coverage was removed.");
  }

  async function setEnabled(enabled: boolean) {
    await run(() => apiPatch("/api/config/freqmapper", { patch: { enabled } }), enabled ? "Coverage is on." : "Coverage is off.");
  }

  async function saveMinutes() {
    const n = parseRefreshMinutes(minutesText);
    if (n === null) {
      setNote({ kind: "error", text: `Use a whole number from ${FM_REFRESH_MIN} to ${FM_REFRESH_MAX}.` });
      return;
    }
    if (status && n === status.refresh_minutes) {
      minutesEdited.current = false;
      return;
    }
    await run(() => apiPatch("/api/config/freqmapper", { patch: { refresh_minutes: n } }), "Refresh time saved.");
    minutesEdited.current = false;
  }

  if (!status) {
    return <div className="mm-settings">{loadFailed ? <div className="node-detail-note">Could not load the FreqMapper settings.</div> : <div className="empty-state">Loading…</div>}</div>;
  }

  return (
    <div className="mm-settings fm-settings">
      <p className="mm-settings-intro">{FM_SETTINGS_INTRO}</p>

      <ApiKeyField
        labelId="fm-key-label"
        keySet={status.key_set}
        busy={busy}
        pastePlaceholder="Paste the FreqMapper API key"
        description="The key is kept on the server and never shown again. Changing or clearing it removes the coverage stored for the old key."
        clearQuestion="Remove the key and its stored coverage?"
        onSave={saveKey}
        onClear={clearKey}
        onEmpty={() => setNote({ kind: "error", text: "Paste the key first." })}
      />

      <label className="settings-field mm-settings-block mm-settings-switch">
        <input type="checkbox" checked={status.enabled} disabled={busy} onChange={(e) => setEnabled(e.target.checked)} />
        <span>Show FreqMapper coverage</span>
        <span className="settings-field-desc">Draws the coverage as hexes on the Live Map. Off by default; while it is off nothing is requested from FreqMapper. It also needs a key. The first time, all of FreqMapper's history is imported, a page at a time.</span>
      </label>

      <div className="settings-field mm-settings-block">
        <label htmlFor="fm-refresh">Refresh every (minutes)</label>
        <div className="mm-settings-keyrow">
          <input
            id="fm-refresh"
            type="number"
            min={FM_REFRESH_MIN}
            max={FM_REFRESH_MAX}
            step={1}
            value={minutesText}
            onChange={(e) => { minutesEdited.current = true; setMinutesText(e.target.value); }}
            onBlur={saveMinutes}
            onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
          />
        </div>
        <span className="settings-field-desc">From {FM_REFRESH_MIN} to {FM_REFRESH_MAX}. However often this is, the app never makes more than {status.request_cap} requests a minute to FreqMapper.</span>
      </div>

      {note && (
        <div className="settings-save-status" data-kind={note.kind} role="status">
          {note.text}
        </div>
      )}

      <h3 className="mm-settings-heading">Status</h3>
      <dl className="mm-settings-status">
        <div><dt>State</dt><dd>{stateText(status.state, status.importing)}</dd></div>
        <div><dt>Last fetch</dt><dd>{isoDateTimeText(status.last_fetch_at)}{status.last_result ? ` (${resultText(status.last_result)})` : ""}</dd></div>
        <div><dt>Events stored</dt><dd>{status.tx_events.toLocaleString("en-US")} verified TX, {status.rx_events.toLocaleString("en-US")} received</dd></div>
        <div><dt>Requests, last minute</dt><dd>{status.requests_last_minute} of {status.request_cap} allowed</dd></div>
        <div><dt>Next fetch</dt><dd>{status.next_poll_at ? isoDateTimeText(status.next_poll_at) : "not scheduled"}</dd></div>
        <div><dt>Last error</dt><dd className={status.last_error ? "mm-settings-error" : undefined}>{status.last_error ?? "none"}</dd></div>
      </dl>
    </div>
  );
}
